/// <amd-module name="Wit/Common/PlanningPane/Components/PlanningSprintCard" />
import { getUtcDate } from "Agile/Common/Utils/Date";
import { IIterationData } from "Agile/Contracts/Common";
import * as React from "react";
import { localeDateFormat } from "VSS/Platform/Date";
import { IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Observer } from "VSSUI/Observer";
import { Pill } from "VSSUI/Pill";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IterationDetailsData } from "Wit/BacklogsHub/Common/Contracts/BacklogsHub.Models";
import { DateRange } from "Wit/Clients/Work/Contracts/Work";
import * as Resources from "Wit/Common/PlanningPane/Resources";
import { WorkItemTypeColorAndIcon, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { MaximumFractionDigits, getNumberInUserFormat } from "Wit/Common/Utilities/FormatUtils";

export interface IPlanningSprintCardProps {
    iteration: IIterationData;
    iterationDetails: IReadonlyObservableValue<IterationDetailsData | undefined>;
    weekends: number[];
    workItemTypeColorAndIcons: IReadonlyObservableArray<WorkItemTypeColorAndIcon>;
    iterationUrl?: string;
    current?: boolean;
    rowIndex?: number;
}

export function PlanningSprintCard({
    iteration,
    iterationDetails,
    weekends,
    workItemTypeColorAndIcons,
    iterationUrl,
    current,
    rowIndex
}: IPlanningSprintCardProps) {
    return (
        <Card className="planning-sprint-card margin-top-8 flex-grow">
            <div className="flex-column flex-grow planning-inner" data-iterationpath={iteration.friendlyPath} data-iterationindex={rowIndex}>
                <div className="flex-row">
                    <div className="flex-grow">{iterationUrl ? <FPSLink href={iterationUrl}>{iteration.name}</FPSLink> : iteration.name}</div>
                    <ConditionalChildren renderChildren={current === true}>
                        <div className="margin-horizontal-8">
                            <Pill className="current-sprint-pill">{Resources.Current}</Pill>
                        </div>
                    </ConditionalChildren>
                    <ConditionalChildren renderChildren={iteration.startDate !== undefined && iteration.finishDate !== undefined}>
                        <span className="flex-self-end">
                            {format(
                                Resources.SprintCardDateFormat,
                                iteration.startDate ? localeDateFormat(iteration.startDate, "d") : undefined,
                                iteration.finishDate ? localeDateFormat(iteration.finishDate, "d") : undefined
                            )}
                        </span>
                    </ConditionalChildren>
                </div>
                <Observer iterationDetails={iterationDetails} workItemTypeColorAndIcons={workItemTypeColorAndIcons}>
                    {(props: { iterationDetails: IterationDetailsData | undefined; workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[] }) => {
                        if (props.iterationDetails === undefined) {
                            return (
                                <div className="flex-row">
                                    <Spinner
                                        className="flex-self-center flex-grow padding-top-16"
                                        size={SpinnerSize.large}
                                        label={Resources.Loading}
                                    />
                                </div>
                            );
                        }

                        return (
                            <React.Fragment>
                                {getIterationEffortContent(iteration, props.iterationDetails, weekends)}
                                {getIterationWorkItemsContent(props.iterationDetails, props.workItemTypeColorAndIcons)}
                            </React.Fragment>
                        );
                    }}
                </Observer>
            </div>
        </Card>
    );
}

function getIterationEffortContent(iteration: IIterationData, iterationDetails: IterationDetailsData, weekends: number[]): JSX.Element | null {
    let numberOfWorkingDays = 0;
    if (iteration.startDate && iteration.finishDate) {
        numberOfWorkingDays = getNumberOfWorkingDays(
            iteration.startDate,
            iteration.finishDate,
            weekends,
            iterationDetails.teamDaysOff.map(dayOff =>
            ({
                start: getUtcDate(dayOff.start),
                end: getUtcDate(dayOff.end)
            }))
        );
    }

    let showEffort = false;
    if (Object.keys(iterationDetails.workItemTypeToCountMap).length > 0) {
        showEffort = true;
    }

    if (numberOfWorkingDays === 0 && !showEffort) {
        return null;
    }

    const iterationDetailsEffort = iterationDetails.effort !== undefined 
        ? getNumberInUserFormat(iterationDetails.effort, MaximumFractionDigits) 
        : "-";

    return (
        <div className="flex-row secondary-text">
            <span className="flex-grow secondary-text">
                {showEffort && format(Resources.PlannedEffort, iterationDetailsEffort)}
            </span>
            {numberOfWorkingDays !== 0 && (
                <span className="secondary-text flex-self-end">{format(Resources.NumberOfWorkingDays, numberOfWorkingDays)}</span>
            )}
        </div>
    );
}

function getIterationWorkItemsContent(iterationDetails: IterationDetailsData, workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[]): JSX.Element {
    const workItemIcons: JSX.Element[] = Object.keys(iterationDetails.workItemTypeToCountMap).map((workItemType: string, index: number) => {
        const workItemTypeColorAndIcon = workItemTypeColorAndIcons.find(
            (colorAndIcon: WorkItemTypeColorAndIcon) => colorAndIcon.workItemTypeName === workItemType
        );

        const cssClass = index > 0 ? "margin-left-16" : undefined;
        return (
            <div className={cssClass} key={index}>
                <WorkItemTypeIcon className="margin-right-4" data={workItemTypeColorAndIcon} />
                <span className="font-weight-semibold">{iterationDetails.workItemTypeToCountMap[workItemType]}</span>
            </div>
        );
    });

    return (
        <div className="flex-row padding-top-8">
            {workItemIcons.length === 0 && <span className="secondary-text">{Resources.NoWorkScheduled}</span>}
            {workItemIcons.length > 0 && workItemIcons}
        </div>
    );
}

/**  Get the number of working days between start date and end date, including both of them.
 *   e.g. --
 *   1. startDate = 20th June Saturday, endDate = 21st June Sunday => return 0
 *   2. startDate = 22nd June Monday, endDate = 22nd June Monday   => return 1
 *   3. startDate = 22nd June Monday, endDate = 23rd June Tuesday  => return 2
 *   4. startDate = 22nd June Monday, endDate = 29th June Monday   => return 6
 * @param startDate The date to start from
 * @param endDate The date to end at
 * @param weekends Array of values (0-6) that represent days considered to be weekends
 * @returns Returns the number of working days between startDate and endDate
 */
function getNumberOfWorkingDays(startDate: Date, endDate: Date, weekends: any[], teamDaysOff: DateRange[]): number {
    let numWorkingDays = 0,
        nextDate = new Date(startDate.getTime());

    while (nextDate <= endDate) {
        // Only count day if it is a working day (not weekend) and the team is working (not a team day off)
        if (weekends.indexOf(nextDate.getDay()) === -1 && !hasOverlapWithTeamDaysOff(nextDate, teamDaysOff)) {
            numWorkingDays += 1;
        }
        nextDate.setDate(nextDate.getDate() + 1);
    }

    return numWorkingDays;
}

/**
 * Check if the provided date is a team day off
 * @param date Date to check
 * @param teamDaysOff Set of date ranges that the team is taking off
 */
function hasOverlapWithTeamDaysOff(date: Date, teamDaysOff: DateRange[]): boolean {
    for (const teamDaysOffRange of teamDaysOff) {
        if (date >= teamDaysOffRange.start && date <= teamDaysOffRange.end) {
            return true;
        }
    }

    return false;
}
